/**
 * Crop the aiming window out of a video or still.
 *
 * The video is displayed with object-fit: cover, so the fractions that
 * describe the on-screen guide do not describe the same region of the source
 * frame. Applying them directly means the person aims at one thing and the
 * decoder reads another.
 *
 * These boxes are the single source of truth — the on-screen guide is painted
 * from the same numbers.
 */

export type ScanBox = { x: number; y: number; w: number; h: number };

/** Wide strip: a 17-character Code 39 VIN is long and short. */
export const BARCODE_BOX: ScanBox = { x: 0.04, y: 0.40, w: 0.92, h: 0.20 };

/** Slightly taller: a windshield plate is a single line with some metal around it. */
export const TEXT_BOX: ScanBox = { x: 0.08, y: 0.36, w: 0.84, h: 0.28 };

export function boxStyle(box: ScanBox): {
  left: string;
  top: string;
  width: string;
  height: string;
} {
  return {
    left: `${box.x * 100}%`,
    top: `${box.y * 100}%`,
    width: `${box.w * 100}%`,
    height: `${box.h * 100}%`,
  };
}

type Source = HTMLVideoElement | HTMLImageElement | HTMLCanvasElement;

function sourceSize(source: Source): { sw: number; sh: number } {
  if (source instanceof HTMLVideoElement) {
    return { sw: source.videoWidth, sh: source.videoHeight };
  }
  if (source instanceof HTMLImageElement) {
    return { sw: source.naturalWidth, sh: source.naturalHeight };
  }
  return { sw: source.width, sh: source.height };
}

function visibleOrigin(
  source: Source,
  sw: number,
  sh: number,
): { originX: number; originY: number; visibleW: number; visibleH: number } | null {
  const cw = source.clientWidth;
  const ch = source.clientHeight;
  if (!cw || !ch) {
    // Not laid out (a detached canvas, or a still we decoded without displaying).
    return { originX: 0, originY: 0, visibleW: sw, visibleH: sh };
  }
  const coverScale = Math.max(cw / sw, ch / sh);
  const visibleW = cw / coverScale;
  const visibleH = ch / coverScale;
  return {
    originX: (sw - visibleW) / 2,
    originY: (sh - visibleH) / 2,
    visibleW,
    visibleH,
  };
}

export function cropToCanvas(
  source: Source,
  canvas: HTMLCanvasElement,
  box: ScanBox,
  opts: { scale?: number; margin?: number; smooth?: boolean; minWidth?: number } = {},
): boolean {
  const { sw, sh } = sourceSize(source);
  if (!sw || !sh) return false;
  const vis = visibleOrigin(source, sw, sh);
  if (!vis) return false;

  let rw = vis.visibleW * box.w;
  let rh = vis.visibleH * box.h;
  let rx = vis.originX + vis.visibleW * box.x;
  let ry = vis.originY + vis.visibleH * box.y;

  const margin = opts.margin ?? 0.14;
  const padX = rw * margin;
  const padY = rh * margin;
  rx = Math.max(0, rx - padX);
  ry = Math.max(0, ry - padY);
  rw = Math.min(sw - rx, rw + padX * 2);
  rh = Math.min(sh - ry, rh + padY * 2);
  if (rw < 8 || rh < 8) return false;

  let scale = opts.scale ?? 1;
  const minWidth = opts.minWidth ?? 0;
  if (minWidth && rw * scale < minWidth) scale = minWidth / rw;

  canvas.width = Math.max(1, Math.round(rw * scale));
  canvas.height = Math.max(1, Math.round(rh * scale));

  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return false;
  ctx.imageSmoothingEnabled = opts.smooth !== false;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source, rx, ry, rw, rh, 0, 0, canvas.width, canvas.height);
  return true;
}

/** Full-frame copy, used for stills where the VIN may be anywhere. */
export function copyToCanvas(
  source: Source,
  canvas: HTMLCanvasElement,
  maxEdge = 1920,
): boolean {
  const { sw, sh } = sourceSize(source);
  if (!sw || !sh) return false;
  const scale = Math.min(1, maxEdge / Math.max(sw, sh));
  canvas.width = Math.max(1, Math.round(sw * scale));
  canvas.height = Math.max(1, Math.round(sh * scale));
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return false;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return true;
}

export function rotateCanvas(source: HTMLCanvasElement, target: HTMLCanvasElement, quarterTurns = 1): boolean {
  const q = ((quarterTurns % 4) + 4) % 4;
  if (q === 0) {
    target.width = source.width;
    target.height = source.height;
    const ctx = target.getContext("2d", { willReadFrequently: true });
    if (!ctx) return false;
    ctx.drawImage(source, 0, 0);
    return true;
  }
  const swap = q === 1 || q === 3;
  target.width = swap ? source.height : source.width;
  target.height = swap ? source.width : source.height;
  const ctx = target.getContext("2d", { willReadFrequently: true });
  if (!ctx) return false;
  ctx.translate(target.width / 2, target.height / 2);
  ctx.rotate((Math.PI / 2) * q);
  ctx.drawImage(source, -source.width / 2, -source.height / 2);
  return true;
}
